import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Bath, Bed, Car, ExternalLink, Loader2, Phone, Ruler } from "lucide-react";
import { getPublicPropertyListing, getScrapedProperty } from "../../../api/collingsData";
import { KIND_COLORS, KIND_LABELS } from "../components/PropertyMapView";
import { OwnerContactList } from "../../dashboard/components/RelistingOwnerContacts";

/**
 * One Homer campaign from the Public Property Search
 * (`/collings-data-properties/homer/:id`), also where its PUBLIC PROPERTY rows
 * in the dashboard search bar land (Christian, Monday 12573204628). Shows the
 * campaign, the listing agency, the portal links and the property's other
 * Homer campaigns (an earlier sale, a withdrawal, a rental).
 *
 * "Owner details" lists the people the office has on file for the address
 * from its spreadsheets (owners, landlords, investors, past vendors and
 * buyers), whether or not the property was ever withdrawn (Christian, Monday
 * 13354971743, 24 Oct 2026).
 *
 * With source="collings" (`/collings-data-properties/collings/:id`) it shows
 * one property from our own scraped data instead: its facts, photos and last
 * sale, with the same address's Homer campaigns underneath, so the address
 * has one profile (Christian, Monday 12573204628, 22 + 24 Oct 2026). A Homer
 * campaign carries that profile's photos and links to it.
 */

function fmtDate(iso) {
    if (!iso) return "";
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

function portalName(url) {
    try {
        const host = new URL(url).hostname.replace(/^www\./, "");
        if (host.includes("realestate.com.au")) return "realestate.com.au";
        if (host.includes("domain.com.au")) return "domain.com.au";
        return host;
    } catch {
        return "Listing";
    }
}

const Fact = ({ label, value }) =>
    value ? (
        <div className="flex justify-between gap-4 border-b border-gray-100 py-2 text-sm last:border-0">
            <span className="text-gray-500">{label}</span>
            <span className="text-right font-semibold text-gray-900">{value}</span>
        </div>
    ) : null;

const PublicPropertyListingPage = ({ source = "homer" }) => {
    const scraped = source === "collings";
    const { id } = useParams();
    const navigate = useNavigate();
    const [row, setRow] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setRow(null);
        setError(null);
        (scraped ? getScrapedProperty(id) : getPublicPropertyListing(id))
            .then((res) => !cancelled && setRow(res.data))
            .catch((err) => {
                if (cancelled) return;
                const gone = scraped ? "This property is no longer in our data." : "This property is no longer in the Homer data.";
                setError(err.response?.status === 404 ? gone : "Could not load this property.");
            });
        return () => {
            cancelled = true;
        };
    }, [id, scraped]);

    const back = (
        <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-[#55C5D0]"
        >
            <ArrowLeft className="h-4 w-4" /> Back
        </button>
    );

    if (error) {
        return (
            <div className="mx-auto max-w-5xl space-y-4 p-4">
                {back}
                <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-600">{error}</div>
            </div>
        );
    }
    if (!row) {
        return (
            <div className="flex justify-center py-20">
                <Loader2 className="h-6 w-6 animate-spin text-[#55C5D0]" />
            </div>
        );
    }

    const color = KIND_COLORS[row.kind] || KIND_COLORS.not_for_sale;
    const mapQuery = row.latitude != null && row.longitude != null ? `${row.latitude},${row.longitude}` : row.address;
    const photos = (row.images || []).map((i) => i.file).filter(Boolean);

    return (
        <div className="mx-auto max-w-6xl space-y-5 p-2 sm:p-4">
            {back}

            <div className="rounded-xl border border-gray-200 bg-white p-5">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full px-2.5 py-0.5 text-xs font-bold text-white" style={{ backgroundColor: color }}>
                        {KIND_LABELS[row.kind] || row.kind_label}
                    </span>
                    <span className="rounded bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                        Public Property
                    </span>
                    {row.property_type ? <span className="text-xs capitalize text-gray-500">{row.property_type}</span> : null}
                </div>
                <h1 className="mt-2 text-xl sm:text-2xl font-bold text-gray-900">{row.address}</h1>
                <p className="mt-1 text-lg font-bold text-[#2b8e97]">{row.price_display || (scraped ? "" : "Contact agent")}</p>
                <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-700">
                    <span className="flex items-center gap-1"><Bed className="h-4 w-4" /> {row.beds || 0} bed</span>
                    <span className="flex items-center gap-1"><Bath className="h-4 w-4" /> {row.baths || 0} bath</span>
                    <span className="flex items-center gap-1"><Car className="h-4 w-4" /> {row.parkings || 0} car</span>
                    {row.land_size ? (
                        <span className="flex items-center gap-1"><Ruler className="h-4 w-4" /> {Math.round(row.land_size)} m²</span>
                    ) : null}
                </div>
                {!scraped && row.collings_profile_url ? (
                    <button
                        type="button"
                        onClick={() => navigate(row.collings_profile_url)}
                        className="mt-3 text-sm font-semibold text-[#2b8e97] hover:underline"
                    >
                        Open the property profile
                    </button>
                ) : null}
            </div>

            {photos.length ? (
                <div className="flex gap-2 overflow-x-auto rounded-xl border border-gray-200 bg-white p-2">
                    {photos.map((src) => (
                        <img
                            key={src}
                            src={src}
                            alt=""
                            loading="lazy"
                            className="h-40 w-60 shrink-0 rounded-lg object-cover"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                        />
                    ))}
                </div>
            ) : null}

            <div className="grid gap-5 lg:grid-cols-2">
                <div className="space-y-5">
                    {(row.owner_contacts || []).length ? (
                        <div className="rounded-xl border border-teal-100 bg-teal-50/60 p-5">
                            <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">
                                Owner details <span className="font-semibold normal-case tracking-normal text-gray-400">· from our records</span>
                            </h2>
                            <OwnerContactList contacts={row.owner_contacts} />
                        </div>
                    ) : null}

                    {scraped ? (
                        <div className="rounded-xl border border-gray-200 bg-white p-5">
                            <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">Property</h2>
                            <Fact label="Last sold" value={(row.last_sold_label || "").replace(/^Last sold /, "")} />
                            <Fact label="Listing status" value={row.status} />
                            <Fact label="Council" value={row.lga} />
                            <Fact label="Suburb" value={`${row.suburb} ${row.state} ${row.postal_code}`} />
                        </div>
                    ) : (
                        <div className="rounded-xl border border-gray-200 bg-white p-5">
                            <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">Campaign</h2>
                            <Fact label="Listed" value={fmtDate(row.listed_date)} />
                            <Fact label="Days on market" value={row.days_on_market != null ? String(row.days_on_market) : ""} />
                            <Fact label="Sold" value={fmtDate(row.sold_date)} />
                            <Fact label="Withdrawn" value={fmtDate(row.withdrawn_date)} />
                            <Fact label="Sale method" value={(row.sale_method || "").replace(/_/g, " ")} />
                            <Fact label="Suburb" value={`${row.suburb} ${row.state} ${row.postal_code}`} />
                        </div>
                    )}

                    {scraped && !row.agency_name && !row.agent_name && !(row.listing_urls || []).length ? null : (
                    <div className="rounded-xl border border-gray-200 bg-white p-5">
                        <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">Listing agency</h2>
                        <Fact label="Agency" value={row.agency_name} />
                        <Fact label="Agent" value={row.agent_name} />
                        {row.agent_phone ? (
                            <div className="flex justify-between gap-4 py-2 text-sm">
                                <span className="text-gray-500">Phone</span>
                                <a href={`tel:${row.agent_phone}`} className="flex items-center gap-1 font-semibold text-[#2b8e97] hover:underline">
                                    <Phone className="h-3.5 w-3.5" /> {row.agent_phone}
                                </a>
                            </div>
                        ) : null}
                        {(row.listing_urls || []).length ? (
                            <div className="mt-3 flex flex-wrap gap-2">
                                {row.listing_urls.map((u) => (
                                    <a
                                        key={u}
                                        href={u}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 rounded-lg bg-[#E8F7F8] px-3 py-1.5 text-xs font-semibold text-[#2b8e97] hover:bg-[#d9f1f3]"
                                    >
                                        <ExternalLink className="h-3.5 w-3.5" /> {portalName(u)}
                                    </a>
                                ))}
                            </div>
                        ) : null}
                    </div>
                    )}

                    {(row.history || []).length ? (
                        <div className="rounded-xl border border-gray-200 bg-white p-5">
                            <h2 className="mb-2 text-sm font-bold uppercase tracking-wide text-gray-500">
                                {scraped ? "Homer campaigns at this address" : "Other campaigns at this address"}
                            </h2>
                            {row.history.map((h) => (
                                <button
                                    key={h.id}
                                    type="button"
                                    onClick={() => navigate(`/collings-data-properties/homer/${h.id}`)}
                                    className="flex w-full items-center justify-between gap-3 border-b border-gray-100 py-2 text-left text-sm last:border-0 hover:text-[#2b8e97]"
                                >
                                    <span className="flex items-center gap-2">
                                        <span
                                            className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
                                            style={{ backgroundColor: KIND_COLORS[h.kind] || KIND_COLORS.not_for_sale }}
                                        >
                                            {KIND_LABELS[h.kind] || h.kind_label}
                                        </span>
                                        {h.price_display}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {fmtDate(h.sold_date || h.withdrawn_date || h.listed_date)}
                                        {h.agency_name ? ` · ${h.agency_name}` : ""}
                                    </span>
                                </button>
                            ))}
                        </div>
                    ) : null}
                </div>

                <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                    <iframe
                        title="Property location"
                        className="h-80 w-full lg:h-full lg:min-h-[420px]"
                        loading="lazy"
                        src={`https://maps.google.com/maps?q=${encodeURIComponent(mapQuery)}&z=16&output=embed`}
                    />
                </div>
            </div>
        </div>
    );
};

export default PublicPropertyListingPage;
